import React, { useEffect, useState } from 'react';
import { runBopi } from '../../services/bopi';
import { getCronStatus, runBnrRates, listExchangeRates, runLitigiiSync, runDailyReport, runAutoTasks, runPontajReminders, runMandatariReport } from '../../services/rapoarte';
import { PONTAJ_ENABLED } from '../../config/features';
import '../Clienti/ClientiPage.css';

const STATUS_STYLE = {
  success: { bg: '#f0fdf4', border: '#86efac', color: '#166534', label: 'Succes' },
  error:   { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', label: 'Eroare' },
  running: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', label: 'In curs' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusCard({ title, log }) {
  const st = STATUS_STYLE[log?.status] || { bg: '#f3f4f6', border: '#e5e7eb', color: '#6b7280', label: 'Niciodata rulat' };
  return (
    <div style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 260 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1a2540' }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: st.color, background: '#fff', border: `1px solid ${st.border}`, borderRadius: 99, padding: '2px 9px' }}>
          {st.label}
        </span>
      </div>
      {log ? (
        <>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 2 }}>{log.message || '—'}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {fmtDateTime(log.finished_at || log.started_at)} · {log.triggered_by === 'manual' ? 'manual' : 'automat'}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: '#9ca3af' }}>Nicio rulare inregistrata inca.</div>
      )}
    </div>
  );
}

function Stat({ label, value, color = '#374151' }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
    </div>
  );
}

export default function RapoartePage() {
  const [cronStatus, setCronStatus] = useState(null);
  const [rates, setRates] = useState([]);

  const loadStatus = () => {
    getCronStatus().then(setCronStatus).catch(() => {});
    listExchangeRates().then(d => setRates(d.rates || [])).catch(() => {});
  };

  useEffect(() => { loadStatus(); }, []);

  // BOPI
  const [bopiStatus, setBopiStatus] = useState('idle');
  const [bopiResult, setBopiResult] = useState(null);
  const [bopiErr, setBopiErr] = useState('');

  const handleBopiStart = async () => {
    setBopiStatus('running');
    setBopiResult(null);
    setBopiErr('');
    try {
      const data = await runBopi();
      setBopiResult(data);
      setBopiStatus('done');
      loadStatus();
    } catch (e) {
      setBopiErr(e?.response?.data?.detail || e?.message || 'Eroare necunoscuta');
      setBopiStatus('error');
      loadStatus();
    }
  };

  // BNR rates
  const [bnrStatus, setBnrStatus] = useState('idle');
  const [bnrErr, setBnrErr] = useState('');

  const handleBnrRun = async () => {
    setBnrStatus('running');
    setBnrErr('');
    try {
      await runBnrRates();
      setBnrStatus('done');
      loadStatus();
    } catch (e) {
      setBnrErr(e?.response?.data?.message || e?.response?.data?.detail || e?.message || 'Eroare necunoscuta');
      setBnrStatus('error');
      loadStatus();
    }
  };

  // Litigii sync (Just.ro)
  const [litigiiStatus, setLitigiiStatus] = useState('idle');
  const [litigiiResult, setLitigiiResult] = useState(null);
  const [litigiiErr, setLitigiiErr] = useState('');

  const handleLitigiiRun = async () => {
    setLitigiiStatus('running');
    setLitigiiResult(null);
    setLitigiiErr('');
    try {
      const data = await runLitigiiSync();
      setLitigiiResult(data.result);
      setLitigiiStatus('done');
      loadStatus();
    } catch (e) {
      // The run can "fail" (non-2xx) while still carrying a useful per-item
      // breakdown (partial success with some record-level errors) — show it
      // instead of just the generic top-level message.
      const payload = e?.response?.data;
      if (payload?.result) {
        setLitigiiResult(payload.result);
        setLitigiiStatus('done');
      } else {
        setLitigiiErr(payload?.message || payload?.detail || e?.message || 'Eroare necunoscuta');
        setLitigiiStatus('error');
      }
      loadStatus();
    }
  };

  // Daily report
  const [reportStatus, setReportStatus] = useState('idle');
  const [reportResult, setReportResult] = useState(null);
  const [reportErr, setReportErr] = useState('');

  const handleReportRun = async () => {
    setReportStatus('running');
    setReportResult(null);
    setReportErr('');
    try {
      const data = await runDailyReport();
      setReportResult(data);
      setReportStatus('done');
      loadStatus();
    } catch (e) {
      const payload = e?.response?.data;
      setReportErr(payload?.message || payload?.detail || e?.message || 'Eroare necunoscuta');
      setReportStatus('error');
      loadStatus();
    }
  };

  // Task-uri automate
  const [autoTasksStatus, setAutoTasksStatus] = useState('idle');
  const [autoTasksResult, setAutoTasksResult] = useState(null);
  const [autoTasksErr, setAutoTasksErr] = useState('');

  const handleAutoTasksRun = async () => {
    setAutoTasksStatus('running');
    setAutoTasksResult(null);
    setAutoTasksErr('');
    try {
      const data = await runAutoTasks();
      setAutoTasksResult(data);
      setAutoTasksStatus('done');
      loadStatus();
    } catch (e) {
      const payload = e?.response?.data;
      setAutoTasksErr(payload?.message || payload?.detail || e?.message || 'Eroare necunoscuta');
      setAutoTasksStatus('error');
      loadStatus();
    }
  };

  // Raport lunar mandatari
  const [mandRepStatus, setMandRepStatus] = useState('idle');
  const [mandRepResult, setMandRepResult] = useState(null);
  const [mandRepErr, setMandRepErr] = useState('');

  const handleMandatariReportRun = async () => {
    setMandRepStatus('running');
    setMandRepResult(null);
    setMandRepErr('');
    try {
      const data = await runMandatariReport();
      setMandRepResult(data);
      setMandRepStatus('done');
      loadStatus();
    } catch (e) {
      const payload = e?.response?.data;
      setMandRepErr(payload?.message || payload?.detail || e?.message || 'Eroare necunoscuta');
      setMandRepStatus('error');
      loadStatus();
    }
  };

  // Reminder pontaj
  const [pontajRemStatus, setPontajRemStatus] = useState('idle');
  const [pontajRemResult, setPontajRemResult] = useState(null);
  const [pontajRemErr, setPontajRemErr] = useState('');

  const handlePontajRemindersRun = async () => {
    setPontajRemStatus('running');
    setPontajRemResult(null);
    setPontajRemErr('');
    try {
      const data = await runPontajReminders();
      setPontajRemResult(data);
      setPontajRemStatus('done');
      loadStatus();
    } catch (e) {
      const payload = e?.response?.data;
      setPontajRemErr(payload?.message || payload?.detail || e?.message || 'Eroare necunoscuta');
      setPontajRemStatus('error');
      loadStatus();
    }
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Rapoarte</h1>
        </div>
      </div>

      <div className="content" style={{ maxWidth: 760 }}>
        {/* ── Cron status overview ── */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
          <StatusCard title="Sincronizare BOPI" log={cronStatus?.bopi_sync} />
          <StatusCard title="Curs valutar BNR" log={cronStatus?.bnr_rates} />
          <StatusCard title="Sincronizare Litigii (Just.ro)" log={cronStatus?.litigii_sync} />
          <StatusCard title="Raport zilnic" log={cronStatus?.daily_report} />
          <StatusCard title="Task-uri automate" log={cronStatus?.auto_tasks} />
          <StatusCard title="Reminder task-uri (email)" log={cronStatus?.task_reminder} />
          {PONTAJ_ENABLED && <StatusCard title="Reminder pontaj" log={cronStatus?.pontaj_reminder} />}
          <StatusCard title="Raport lunar mandatari" log={cronStatus?.mandatari_report} />
        </div>

        {/* ── BOPI ── */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Porneste BOPI</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Rezolva automat CAPTCHA-ul OSIM si preia aparitiile BOPI pentru toate marcile inregistrate.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleBopiStart} disabled={bopiStatus === 'running'}>
              {bopiStatus === 'running' ? 'Se proceseaza…' : 'Porneste'}
            </button>
          </div>

          {bopiStatus === 'running' && (
            <div style={{ fontSize: 13, color: '#6b7280' }}>Se rezolva CAPTCHA OSIM si se preiau datele BOPI…</div>
          )}

          {bopiStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
              <strong>Eroare:</strong> {bopiErr}
            </div>
          )}

          {bopiStatus === 'done' && bopiResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>Procesare finalizata</div>
              <div style={{ display: 'flex', gap: 24, marginBottom: bopiResult.errors?.length ? 16 : 0 }}>
                <Stat label="Total proceduri" value={bopiResult.total} />
                <Stat label="Procesate cu succes" value={bopiResult.success} color="#166534" />
                {bopiResult.failed > 0 && <Stat label="Erori" value={bopiResult.failed} color="#991b1b" />}
              </div>
              {bopiResult.errors?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Detalii erori</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {bopiResult.errors.map((e, i) => (
                      <li key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                        <strong>{e.client}</strong> ({e.deposit_nr || e.idappli}) — {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Cursul zilei ── */}
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Cursul zilei</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Cursurile BNR se preiau automat in fiecare dimineata si se salveaza local — facturarea foloseste
            exclusiv aceste valori, fara apeluri catre internet la emiterea facturilor.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleBnrRun} disabled={bnrStatus === 'running'}>
              {bnrStatus === 'running' ? 'Se preiau cursurile…' : 'Preia cursurile acum'}
            </button>
          </div>

          {bnrStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b', marginBottom: 16 }}>
              <strong>Eroare:</strong> {bnrErr}
            </div>
          )}

          {rates.length > 0 && (
            <div className="clients-card">
              <table className="clients-table">
                <thead>
                  <tr>
                    <th>Moneda</th>
                    <th>Curs (RON)</th>
                    <th>Data BNR</th>
                    <th>Actualizat</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map(r => (
                    <tr key={r.currency}>
                      <td>{r.currency}</td>
                      <td>{r.rate.toFixed(4)}</td>
                      <td>{r.rate_date || '—'}</td>
                      <td>{fmtDateTime(r.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Sincronizare Litigii (Just.ro) ── */}
        <div style={{ marginTop: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Sincronizare Litigii (Just.ro)</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Preia sedintele de judecata pentru toate dosarele de instanta de pe portalul national Just.ro.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleLitigiiRun} disabled={litigiiStatus === 'running'}>
              {litigiiStatus === 'running' ? 'Se sincronizeaza…' : 'Sincronizeaza acum'}
            </button>
          </div>

          {litigiiStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
              <strong>Eroare:</strong> {litigiiErr}
            </div>
          )}

          {litigiiStatus === 'done' && litigiiResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>Sincronizare finalizata</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: litigiiResult.errors?.length ? 16 : 0 }}>
                <Stat label="Total dosare" value={litigiiResult.total} />
                <Stat label="Sincronizate" value={litigiiResult.updated} color="#166534" />
                <Stat label="Negasite" value={litigiiResult.not_found} />
                <Stat label="Sarite" value={litigiiResult.skipped} />
                {litigiiResult.errors?.length > 0 && <Stat label="Erori" value={litigiiResult.errors.length} color="#991b1b" />}
              </div>
              {litigiiResult.errors?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Detalii erori</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {litigiiResult.errors.map((e, i) => (
                      <li key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                        <strong>{e.client}</strong> — {e.denumire} ({e.numar}) — {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {litigiiResult.skipped_list?.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                    Detalii sarite ({litigiiResult.skipped_list.length})
                  </summary>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 16 }}>
                    {litigiiResult.skipped_list.map((s, i) => (
                      <li key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                        <strong>{s.client}</strong> — {s.denumire} ({s.numar || 'fara numar'}) — {s.motiv}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {litigiiResult.not_found_list?.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                    Detalii negasite ({litigiiResult.not_found_list.length})
                  </summary>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 16 }}>
                    {litigiiResult.not_found_list.map((n, i) => (
                      <li key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                        <strong>{n.client}</strong> — {n.denumire} ({n.numar})
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* ── Raport zilnic ── */}
        <div style={{ marginTop: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Raport zilnic</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Genereaza raportul zilnic (clienti, task-uri, facturare, alerte, anomalii de date) si il trimite
            pe email. Momentan trimis intotdeauna la cretu.bogdan@yahoo.ro — nu exista inca un camp de email pe
            conturile de utilizator.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleReportRun} disabled={reportStatus === 'running'}>
              {reportStatus === 'running' ? 'Se genereaza…' : 'Genereaza acum'}
            </button>
          </div>

          {reportStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
              <strong>Eroare:</strong> {reportErr}
            </div>
          )}

          {reportStatus === 'done' && reportResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>
                Raport generat si trimis
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>{reportResult.message}</div>
              {reportResult.sections?.counters && (
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <Stat label="Clienti" value={reportResult.sections.counters.clients} />
                  <Stat label="Task-uri active" value={reportResult.sections.counters.active_tasks} />
                  <Stat label="Facturi neincasate" value={reportResult.sections.counters.unpaid_invoices} />
                  <Stat label="Alerte active" value={reportResult.sections.counters.active_alerts} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Task-uri automate ── */}
        <div style={{ marginTop: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Task-uri automate</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Genereaza task-uri [AI] din alertele active, termene/sedinte apropiate si facturare (restante,
            fara scadenta). Cele fara responsabil cunoscut sunt propuse rolului Avocat/Consilier (dosare) sau
            Facturare (facturi) si apar pe homepage pentru asumare. Alertele depasite cu peste 500 de zile
            sunt ignorate. Nu este inca in crontab — doar rulare manuala, pana la revizuirea rezultatelor reale.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleAutoTasksRun} disabled={autoTasksStatus === 'running'}>
              {autoTasksStatus === 'running' ? 'Se genereaza…' : 'Genereaza acum'}
            </button>
          </div>

          {autoTasksStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
              <strong>Eroare:</strong> {autoTasksErr}
            </div>
          )}

          {autoTasksStatus === 'done' && autoTasksResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>Generare finalizata</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Stat label="Total situatii" value={autoTasksResult.detail?.total_candidates} />
                <Stat label="Task-uri create" value={autoTasksResult.detail?.created} color="#166534" />
                <Stat label="Deja existente" value={autoTasksResult.detail?.skipped_duplicate} />
                <Stat label="Ignorate (500+ zile)" value={autoTasksResult.detail?.skipped_too_old} />
              </div>
            </div>
          )}
        </div>

        {/* ── Reminder pontaj ── */}
        {PONTAJ_ENABLED && (
          <div style={{ marginTop: 40 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Reminder pontaj</div>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
              Trimite un email fiecarui utilizator care nu are rol de coordonator si nu a trimis inca
              pontajul de azi. Gandit sa ruleze o data pe zi lucratoare la ora 16:00 — nu este inca
              in crontab, doar rulare manuala.
            </p>

            <div style={{ marginBottom: 20 }}>
              <button className="btn btn-primary btn-sm" type="button" onClick={handlePontajRemindersRun} disabled={pontajRemStatus === 'running'}>
                {pontajRemStatus === 'running' ? 'Se trimit...' : 'Trimite remindere acum'}
              </button>
            </div>

            {pontajRemStatus === 'error' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
                <strong>Eroare:</strong> {pontajRemErr}
              </div>
            )}

            {pontajRemStatus === 'done' && pontajRemResult && (
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>Trimitere finalizata</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <Stat label="Remindere trimise" value={pontajRemResult.result?.reminded?.length || 0} color="#166534" />
                  <Stat label="Deja trimisesera" value={pontajRemResult.result?.skipped_submitted?.length || 0} />
                  <Stat label="Fara email" value={pontajRemResult.result?.skipped_no_email?.length || 0} />
                  {pontajRemResult.result?.errors?.length > 0 && (
                    <Stat label="Erori" value={pontajRemResult.result.errors.length} color="#991b1b" />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Raport lunar mandatari externi ── */}
        <div style={{ marginTop: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginBottom: 6 }}>Raport lunar mandatari externi</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
            Trimite pe email, catre fiecare coordonator/avocat coordonator cu adresa configurata, situatia
            procedurilor active prin mandatar extern, instructiunile neconfirmate, cheltuielile de oficiu
            neachitate si indicatorii pe mandatar. Gandit sa ruleze o data pe luna — nu este inca in crontab,
            doar rulare manuala.
          </p>

          <div style={{ marginBottom: 20 }}>
            <button className="btn btn-primary btn-sm" type="button" onClick={handleMandatariReportRun} disabled={mandRepStatus === 'running'}>
              {mandRepStatus === 'running' ? 'Se trimite...' : 'Trimite raportul acum'}
            </button>
          </div>

          {mandRepStatus === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
              <strong>Eroare:</strong> {mandRepErr}
            </div>
          )}

          {mandRepStatus === 'done' && mandRepResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 12 }}>Trimitere finalizata</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Stat label="Proceduri active" value={mandRepResult.result?.sections?.proceduri_active?.length || 0} />
                <Stat label="Instructiuni neconfirmate" value={mandRepResult.result?.sections?.instructiuni_neconfirmate?.length || 0} />
                <Stat label="Cheltuieli neachitate" value={mandRepResult.result?.sections?.cheltuieli_neachitate?.length || 0} />
                <Stat label="Destinatari" value={mandRepResult.result?.recipients?.length || 0} color="#166534" />
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
